import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, Link as RouterLink } from 'react-router-dom';
import { X, Link2, Camera, PenLine, ArrowRight, Loader2, Lock, BedDouble, Bath, Maximize2, DollarSign, MapPin, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const C = {
  bg:      '#0E1520', surface: '#16202E', surfaceAlt: '#1C2A3A',
  coral:   '#E07A5F', gold:    '#C4A87A', text: '#F8F9FA',
  muted:   '#C8D3DF', border:  'rgba(255,255,255,0.08)',
  green:   'rgba(52,211,153,0.88)', amber: 'rgba(251,191,36,0.82)',
};
const editorial = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const sans       = { fontFamily: "'Outfit', system-ui, sans-serif" };
const serif      = { fontFamily: "'Playfair Display', Georgia, serif" };

const FREE_IMPORT_LIMIT = 1;
function getImportsUsed() {
  try { return parseInt(localStorage.getItem('gl_imports_used') || '0', 10); } catch { return 0; }
}
function isPremium() { return localStorage.getItem('gl_premium') === '1'; }

function formatPrice(p) {
  if (!p && p !== 0) return null;
  if (p >= 1_000_000) { const m = p / 1_000_000; return '$' + (m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)) + 'M'; }
  if (p >= 1_000) return '$' + Math.round(p / 1_000) + 'K';
  return '$' + p;
}

// ─── Step 1: Choose method ─────────────────────────────────────────────────────
function StepChoose({ onChoose }) {
  const methods = [
    { key: 'url',        icon: Link2,    label: 'Paste a link',     sub: 'Zillow, Redfin, Realtor, any listing URL' },
    { key: 'screenshot', icon: Camera,   label: 'Upload a photo',   sub: 'Screenshot of any listing page' },
    { key: 'manual',     icon: PenLine,  label: 'Enter manually',   sub: 'Type in what you know' },
  ];
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest mb-2"
        style={{ ...sans, color: 'rgba(196,168,122,0.6)', letterSpacing: '0.14em' }}>
        Found a home you like?
      </p>
      <h2 style={{ ...editorial, color: C.text, fontSize: '1.95rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '0.4rem' }}>
        Paste the link.
      </h2>
      <p className="mb-7 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.55)', lineHeight: 1.75 }}>
        We'll tell you if it actually fits.
      </p>
      <div className="space-y-3">
        {methods.map(m => (
          <motion.button key={m.key} whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }}
            onClick={() => onChoose(m.key)}
            data-testid={'import-method-' + m.key}
            className="w-full flex items-center gap-4 px-5 py-4 rounded-2xl text-left transition-all"
            style={{ background: C.surfaceAlt, border: '1px solid ' + C.border }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(196,168,122,0.3)'; e.currentTarget.style.background = '#1E2D40'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surfaceAlt; }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(196,168,122,0.1)', border: '1px solid rgba(196,168,122,0.2)' }}>
              <m.icon size={18} style={{ color: C.gold }} />
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ ...sans, color: C.text }}>{m.label}</p>
              <p className="text-xs mt-0.5" style={{ ...sans, color: 'rgba(200,211,223,0.45)' }}>{m.sub}</p>
            </div>
            <ArrowRight size={14} className="ml-auto" style={{ color: 'rgba(200,211,223,0.35)' }} />
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 2a: URL input ────────────────────────────────────────────────────────
function StepURL({ onAnalyze, loading }) {
  const [url, setUrl] = useState('');
  return (
    <div>
      <h2 style={{ ...editorial, color: C.text, fontSize: '1.8rem', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
        Paste the listing URL
      </h2>
      <p className="mb-6 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)', lineHeight: 1.7 }}>
        Works with Zillow, Redfin, Realtor.com, Trulia, and most listing pages.
      </p>
      <input
        type="url" value={url} onChange={e => setUrl(e.target.value)}
        placeholder="https://www.zillow.com/homedetails/..."
        data-testid="import-url-input"
        className="w-full px-4 py-3.5 rounded-xl text-sm mb-4 outline-none transition-all"
        style={{ ...sans, background: C.surfaceAlt, border: '1px solid rgba(255,255,255,0.12)', color: C.text }}
        onFocus={e => { e.target.style.borderColor = 'rgba(196,168,122,0.4)'; }}
        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
        onKeyDown={e => { if (e.key === 'Enter' && url.trim()) onAnalyze(url.trim()); }}
      />
      <button
        onClick={() => onAnalyze(url.trim())}
        disabled={!url.trim() || loading}
        data-testid="import-url-analyze-btn"
        className="w-full py-3.5 rounded-full font-semibold text-sm transition-all disabled:opacity-40"
        style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em' }}>
        {loading ? <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />Reading the listing…</span> : 'Analyze →'}
      </button>
    </div>
  );
}

// ─── Step 2b: Screenshot upload ───────────────────────────────────────────────
function StepScreenshot({ onAnalyze, loading }) {
  const [preview, setPreview] = useState(null);
  const [b64, setB64]         = useState(null);
  const [mime, setMime]       = useState('image/jpeg');
  const fileRef               = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const full = ev.target.result;
      // full = "data:image/jpeg;base64,/9j/..."
      const [header, data] = full.split(',');
      const mimeMatch = header.match(/:(.*?);/);
      setMime(mimeMatch ? mimeMatch[1] : 'image/jpeg');
      setB64(data);
      setPreview(full);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) handleFile(file);
  }, []); // eslint-disable-line

  return (
    <div>
      <h2 style={{ ...editorial, color: C.text, fontSize: '1.8rem', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
        Upload a screenshot
      </h2>
      <p className="mb-5 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)', lineHeight: 1.7 }}>
        Take a screenshot of any listing. We'll read the details from it.
      </p>

      {!preview ? (
        <div
          className="rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all"
          style={{ border: '2px dashed rgba(196,168,122,0.25)', background: C.surfaceAlt, minHeight: '180px' }}
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          data-testid="import-screenshot-dropzone">
          <Camera size={28} className="mb-3" style={{ color: 'rgba(196,168,122,0.4)' }} />
          <p className="text-sm font-semibold mb-1" style={{ ...sans, color: C.muted }}>Drop screenshot here</p>
          <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.4)' }}>or click to choose a file</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={e => handleFile(e.target.files?.[0])} />
        </div>
      ) : (
        <div className="relative rounded-2xl overflow-hidden mb-4" style={{ maxHeight: '220px' }}>
          <img src={preview} alt="preview" className="w-full object-cover" style={{ maxHeight: '220px' }} />
          <button onClick={() => { setPreview(null); setB64(null); }}
            className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(14,21,32,0.85)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <X size={13} style={{ color: C.muted }} />
          </button>
        </div>
      )}

      <button
        onClick={() => onAnalyze(b64, mime)}
        disabled={!b64 || loading}
        data-testid="import-screenshot-analyze-btn"
        className="w-full py-3.5 rounded-full font-semibold text-sm transition-all mt-4 disabled:opacity-40"
        style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em' }}>
        {loading ? <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />Analyzing image…</span> : 'Analyze →'}
      </button>
    </div>
  );
}

// ─── Step 2c: Manual entry ────────────────────────────────────────────────────
const PROPERTY_TYPES = ['single_family','condo','townhouse','multi_family','land','mobile','farm_ranch','villa'];

function StepManual({ onSubmit, loading }) {
  const [form, setForm] = useState({
    address: '', city: '', state: '', price: '', beds: '', baths: '',
    sqft: '', property_type: 'single_family', has_pool: false, has_garage: false,
    has_solar: false, has_hoa: false, hoa_amount: '', description: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const labelStyle = { ...sans, color: 'rgba(200,211,223,0.55)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.4rem', display: 'block' };
  const inputStyle = { ...sans, background: C.surfaceAlt, border: '1px solid rgba(255,255,255,0.1)', color: C.text, borderRadius: '0.75rem', padding: '0.6rem 0.85rem', fontSize: '0.85rem', width: '100%', outline: 'none' };

  return (
    <div>
      <h2 style={{ ...editorial, color: C.text, fontSize: '1.8rem', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '0.4rem' }}>
        Enter what you know
      </h2>
      <p className="mb-5 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>Required: address, price, beds, baths.</p>

      <div className="space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={labelStyle}>Address</label>
            <input style={inputStyle} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Oak St" data-testid="manual-address" />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Scottsdale" data-testid="manual-city" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label style={labelStyle}>State</label>
            <input style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)} placeholder="AZ" maxLength={2} data-testid="manual-state" />
          </div>
          <div>
            <label style={labelStyle}>Price ($)</label>
            <input style={inputStyle} type="number" value={form.price} onChange={e => set('price', e.target.value)} placeholder="750000" data-testid="manual-price" />
          </div>
          <div>
            <label style={labelStyle}>Sqft</label>
            <input style={inputStyle} type="number" value={form.sqft} onChange={e => set('sqft', e.target.value)} placeholder="2100" data-testid="manual-sqft" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label style={labelStyle}>Beds</label>
            <input style={inputStyle} type="number" value={form.beds} onChange={e => set('beds', e.target.value)} placeholder="3" data-testid="manual-beds" />
          </div>
          <div>
            <label style={labelStyle}>Baths</label>
            <input style={inputStyle} type="number" step="0.5" value={form.baths} onChange={e => set('baths', e.target.value)} placeholder="2" data-testid="manual-baths" />
          </div>
          <div>
            <label style={labelStyle}>Type</label>
            <select style={{ ...inputStyle, appearance: 'none' }} value={form.property_type} onChange={e => set('property_type', e.target.value)} data-testid="manual-type">
              {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>

        {/* Feature toggles */}
        <div>
          <label style={labelStyle}>Features</label>
          <div className="flex flex-wrap gap-2">
            {[['pool','Pool'],['garage','Garage'],['solar','Solar'],['has_hoa','HOA']].map(([k, label]) => (
              <button key={k} type="button"
                onClick={() => set(k.startsWith('has_') ? k : 'has_' + k, !form[k.startsWith('has_') ? k : 'has_' + k])}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{
                  ...sans,
                  background: form[k.startsWith('has_') ? k : 'has_' + k] ? 'rgba(196,168,122,0.18)' : 'rgba(255,255,255,0.04)',
                  color: form[k.startsWith('has_') ? k : 'has_' + k] ? C.gold : 'rgba(200,211,223,0.5)',
                  border: '1px solid ' + (form[k.startsWith('has_') ? k : 'has_' + k] ? 'rgba(196,168,122,0.35)' : 'rgba(255,255,255,0.1)'),
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => {
            const data = {
              ...form,
              price: form.price ? parseFloat(form.price) : null,
              beds: form.beds ? parseInt(form.beds) : null,
              baths: form.baths ? parseFloat(form.baths) : null,
              sqft: form.sqft ? parseInt(form.sqft) : null,
              hoa_amount: form.hoa_amount ? parseFloat(form.hoa_amount) : null,
              features: [
                form.has_pool && 'pool', form.has_garage && 'garage',
                form.has_solar && 'solar', !form.has_hoa && 'no_hoa',
              ].filter(Boolean),
            };
            onSubmit(data);
          }}
          disabled={loading || !form.city || !form.price}
          data-testid="manual-submit-btn"
          className="w-full py-3.5 rounded-full font-semibold text-sm transition-all disabled:opacity-40"
          style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em' }}>
          {loading ? <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />Processing…</span> : 'See my match →'}
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Review extracted data ────────────────────────────────────────────
function StepReview({ result, onConfirm, loading }) {
  const ex = result.extracted || {};
  const missing = result.missing_fields || [];
  const [edits, setEdits] = useState({});
  const set = (k, v) => setEdits(e => ({ ...e, [k]: v }));

  const merged = { ...ex, ...edits };
  const stillMissing = missing.filter(f => !edits[f] && !ex[f]);

  const inputStyle = { ...sans, background: C.surfaceAlt, border: '1px solid rgba(255,255,255,0.1)', color: C.text, borderRadius: '0.75rem', padding: '0.55rem 0.85rem', fontSize: '0.85rem', width: '100%', outline: 'none' };
  const labelStyle = { ...sans, color: 'rgba(200,211,223,0.55)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.35rem', display: 'block' };

  return (
    <div>
      <h2 style={{ ...editorial, color: C.text, fontSize: '1.8rem', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '0.35rem' }}>
        Review the details
      </h2>
      {missing.length > 0 && (
        <div className="flex items-start gap-2 mb-4 px-3.5 py-3 rounded-xl"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#FBBF24' }} />
          <p className="text-xs" style={{ ...sans, color: 'rgba(251,191,36,0.85)', lineHeight: 1.6 }}>
            Fill in {stillMissing.join(', ')} for a more accurate match score.
          </p>
        </div>
      )}

      <div className="space-y-3 mb-5">
        <div className="grid grid-cols-2 gap-3">
          {[['address','Address','text'],['city','City','text'],['price','Price ($)','number'],['beds','Beds','number'],['baths','Baths','number'],['sqft','Sqft','number']].map(([k, label, type]) => (
            <div key={k} className={missing.includes(k) && !edits[k] ? 'col-span-1' : 'col-span-1'}>
              <label style={{ ...labelStyle, color: missing.includes(k) && !edits[k] ? 'rgba(251,191,36,0.7)' : labelStyle.color }}>
                {label} {missing.includes(k) && !edits[k] ? '⚠' : merged[k] ? '✓' : ''}
              </label>
              <input style={{ ...inputStyle, borderColor: missing.includes(k) && !edits[k] ? 'rgba(251,191,36,0.3)' : 'rgba(255,255,255,0.1)' }}
                type={type} defaultValue={ex[k] || ''} placeholder={label}
                onChange={e => set(k, type === 'number' ? parseFloat(e.target.value) || null : e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={() => onConfirm(edits)} disabled={loading}
        data-testid="review-confirm-btn"
        className="w-full py-3.5 rounded-full font-semibold text-sm transition-all disabled:opacity-40"
        style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em' }}>
        {loading ? <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />Scoring…</span> : 'See my match →'}
      </button>
    </div>
  );
}

// ─── Step 4: Match result ──────────────────────────────────────────────────────
function StepResult({ result, onSave, onReset }) {
  const ex      = result.extracted || {};
  const listing = result.listing   || {};
  const score   = result.match_score || 0;
  const reasons = result.match_reasons || [];
  const conflicts = result.match_conflicts || [];
  const photo   = (ex.photos || [])[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80';

  const color = score >= 80 ? C.coral : score >= 65 ? C.gold : 'rgba(200,211,223,0.5)';
  const label = score >= 90 ? 'Strong match' : score >= 80 ? "You'll like this" : score >= 68 ? 'Pretty close' : score >= 55 ? `${score}% match` : 'Possible match';
  const canSave = isPremium() || getImportsUsed() < FREE_IMPORT_LIMIT;

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest mb-3"
        style={{ ...sans, color: 'rgba(196,168,122,0.55)', letterSpacing: '0.14em' }}>
        Match result
      </p>

      {/* Photo + score */}
      <div className="relative rounded-2xl overflow-hidden mb-4" style={{ height: '180px' }}>
        <img src={photo} alt="listing" className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'; }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(14,21,32,0.95) 0%, transparent 55%)' }} />
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-semibold"
          style={{ ...sans, background: 'rgba(12,18,28,0.85)', backdropFilter: 'blur(10px)', color, border: '1px solid ' + color + '40' }}>
          {label}
        </div>
        <div className="absolute bottom-3 left-4 right-4">
          <p className="font-semibold text-sm" style={{ ...sans, color: C.text }}>
            {ex.address || listing.address || 'Imported home'}
          </p>
          <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.6)' }}>
            {ex.city || listing.city}{ex.state ? ', ' + ex.state : ''}
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mb-4 px-1">
        {ex.price && <span className="font-bold text-lg" style={{ ...serif, color: C.text }}>{formatPrice(ex.price)}</span>}
        <div className="flex items-center gap-3 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.55)' }}>
          {ex.beds && <span className="flex items-center gap-1"><BedDouble size={11} />{ex.beds}bd</span>}
          {ex.baths && <span className="flex items-center gap-1"><Bath size={11} />{ex.baths}ba</span>}
          {ex.sqft && <span className="flex items-center gap-1"><Maximize2 size={11} />{ex.sqft?.toLocaleString()}</span>}
        </div>
      </div>

      {/* Match breakdown */}
      {reasons.length > 0 && (
        <div className="mb-3">
          <p className="mb-1.5" style={{ ...sans, color: 'rgba(52,211,153,0.45)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Why this works
          </p>
          {reasons.slice(0, 3).map((r, i) => (
            <div key={i} className="flex items-start gap-2 mb-1" style={{ ...sans, color: C.green, fontSize: '0.8rem', lineHeight: 1.5 }}>
              <span style={{ color: '#34D399', fontSize: '0.55rem', marginTop: '0.35rem', flexShrink: 0 }}>&#10004;</span>{r}
            </div>
          ))}
        </div>
      )}
      {conflicts.length > 0 && (
        <div className="mb-4">
          <p className="mb-1.5" style={{ ...sans, color: 'rgba(251,191,36,0.4)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Still missing
          </p>
          {conflicts.slice(0, 2).map((c, i) => (
            <div key={i} className="flex items-start gap-2 mb-1" style={{ ...sans, color: C.amber, fontSize: '0.8rem', lineHeight: 1.5 }}>
              <span style={{ color: '#FBBF24', fontSize: '0.55rem', marginTop: '0.35rem', flexShrink: 0 }}>&#9702;</span>{c}
            </div>
          ))}
        </div>
      )}

      {/* CTAs */}
      {canSave ? (
        <button onClick={onSave} data-testid="import-save-btn"
          className="w-full py-3 rounded-full font-semibold text-sm mb-2 transition-all"
          style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 20px rgba(224,122,95,0.25)' }}>
          Save this home
        </button>
      ) : (
        <div className="rounded-2xl px-4 py-4 mb-2 text-center"
          style={{ background: 'rgba(196,168,122,0.08)', border: '1px solid rgba(196,168,122,0.2)' }}>
          <Lock size={14} className="mx-auto mb-1.5" style={{ color: C.gold }} />
          <p className="text-xs font-semibold mb-1" style={{ ...sans, color: C.gold }}>You've used your free import.</p>
          <p className="text-xs mb-3" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>Unlock unlimited imports to save this home.</p>
          <RouterLink to="/upgrade"
            className="inline-block px-5 py-2 rounded-full text-xs font-semibold transition-all"
            style={{ ...sans, background: C.coral, color: '#fff' }}>
            Unlock saves
          </RouterLink>
        </div>
      )}
      <button onClick={onReset}
        className="w-full py-2.5 text-xs transition-all"
        style={{ ...sans, color: 'rgba(200,211,223,0.4)', background: 'none', border: 'none' }}>
        Add another home
      </button>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function ImportHomeModal({ onClose, authFetch, onImported }) {
  const [step,    setStep]    = useState('choose');   // choose | url | screenshot | manual | loading | review | result
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const callImport = async (body) => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API}/api/buyer/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not process this listing.');
      setResult(data);
      // increment usage
      const used = getImportsUsed();
      localStorage.setItem('gl_imports_used', String(used + 1));
      setStep('result');
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
    setLoading(false);
  };

  const handleURLAnalyze = async (url) => {
    await callImport({ source_type: 'url', source_url: url });
  };

  const handleScreenshotAnalyze = async (b64, mime) => {
    await callImport({ source_type: 'screenshot', image_base64: b64, image_mime: mime });
  };

  const handleManualSubmit = async (data) => {
    await callImport({ source_type: 'manual', manual_data: data });
  };

  const handleReviewConfirm = async (edits) => {
    if (!result?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API}/api/buyer/imports/${result.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edits),
      });
      const data = await res.json();
      setResult(data);
      setStep('result');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleSave = () => {
    if (onImported) onImported(result);
    onClose();
  };

  const reset = () => { setStep('choose'); setResult(null); setError(''); };

  // Show review step if there are missing fields
  const handleExtracted = (data) => {
    setResult(data);
    if (data.missing_fields?.length > 0) setStep('review');
    else setStep('result');
  };

  // Redirect callImport to go through review if missing fields
  const callImportWithReview = async (body) => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API}/api/buyer/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not process this listing.');
      const used = getImportsUsed();
      localStorage.setItem('gl_imports_used', String(used + 1));
      handleExtracted(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: 'rgba(7,12,22,0.88)', backdropFilter: 'blur(18px)' }}
      onClick={onClose}>

      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 32 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{ background: C.surface, border: '1px solid rgba(196,168,122,0.15)', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
        data-testid="import-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          {step !== 'choose' && step !== 'result' && (
            <button onClick={() => step === 'review' ? setStep('choose') : setStep('choose')}
              className="text-xs transition-opacity hover:opacity-70"
              style={{ ...sans, color: 'rgba(200,211,223,0.4)', background: 'none', border: 'none' }}>
              ← Back
            </button>
          )}
          {(step === 'choose' || step === 'result') && <div />}
          <button onClick={onClose} data-testid="import-modal-close"
            className="ml-auto w-7 h-7 rounded-full flex items-center justify-center transition-all hover:bg-white/10"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <X size={13} style={{ color: C.muted }} />
          </button>
        </div>

        <div className="px-6 pb-7">
          {error && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 mb-4 px-3.5 py-3 rounded-xl"
              style={{ background: 'rgba(224,122,95,0.1)', border: '1px solid rgba(224,122,95,0.25)' }}>
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" style={{ color: C.coral }} />
              <p className="text-xs" style={{ ...sans, color: 'rgba(224,122,95,0.9)', lineHeight: 1.6 }}>{error}</p>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.22 }}>

              {step === 'choose' && <StepChoose onChoose={s => setStep(s)} />}
              {step === 'url' && (
                <StepURL loading={loading}
                  onAnalyze={(url) => callImportWithReview({ source_type: 'url', source_url: url })} />
              )}
              {step === 'screenshot' && (
                <StepScreenshot loading={loading}
                  onAnalyze={(b64, mime) => callImportWithReview({ source_type: 'screenshot', image_base64: b64, image_mime: mime })} />
              )}
              {step === 'manual' && (
                <StepManual loading={loading}
                  onSubmit={(data) => callImportWithReview({ source_type: 'manual', manual_data: data })} />
              )}
              {step === 'review' && result && (
                <StepReview result={result} loading={loading} onConfirm={handleReviewConfirm} />
              )}
              {step === 'result' && result && (
                <StepResult result={result} onSave={handleSave} onReset={reset} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
